import {
  createNanoEvents,
  DefaultEvents,
  Emitter,
  Unsubscribe,
} from 'nanoevents';
import { v4 as uuidv4 } from 'uuid';

type EventsManager = {
  on: (event: string, cb: (args: any) => void, scope?: string) => Unsubscribe;
  once: (event: string, cb: (args: any) => void, scope?: string) => Unsubscribe;
  emit: (event: string, args?: any) => void;
  getLastEmittedValue: (event: string) => any | null;
  getEvents: () => Emitter<DefaultEvents>['events'];
  events: Emitter<DefaultEvents>['events'];
  unbindAll: () => void;
  unbindByScope: (scope: string) => void;
};

export type { Unsubscribe };

export const EventsManager = (): EventsManager => {
  let instance: EventsManager | null = (window as any).events_manager || null;
  if (instance) {
    return instance;
  }

  const DEBUG = import.meta.env.VITE_DEBUG_EVENTS_MANAGER === 'true';

  const activeSubscriptions = new Map();
  const lastEmittedValues = new Map();

  const scopedEvents: { [scope: string]: Unsubscribe[] } = {};

  let emitter = createNanoEvents();

  const addToScope = (scope: string, unbind: Unsubscribe) => {
    if (!scopedEvents[scope]) {
      scopedEvents[scope] = [];
    }
    scopedEvents[scope].push(unbind);
  };

  const getLastEmittedValue = (event: string) => {
    return lastEmittedValues.get(event) || null;
  };

  const on = (
    event: string,
    cb: (args: any) => void,
    scope?: string
  ): Unsubscribe => {
    const unbind = emitter.on(event, (args) => cb(args));

    if (scope) {
      addToScope(scope, unbind);
    }

    if (DEBUG) {
      activeSubscriptions.set(event + '-' + uuidv4(), true);
      console.info(
        `Active subscriptions: %c${activeSubscriptions.size}`,
        'font-weight: bold'
      );
      console.info('Subscribing to: %c' + event + '-' + uuidv4(), 'color: red');
      // console.info([...activeSubscriptions.keys()]);
    }

    return () => {
      if (DEBUG) {
        activeSubscriptions.delete(event + '-' + uuidv4());
        console.info(
          `Active subscriptions: %c${activeSubscriptions.size}`,
          'font-weight: bold'
        );
        console.info(
          'Unsubscribing from: %c' + event + '-' + uuidv4(),
          'color: green'
        );
        // console.info([...activeSubscriptions.keys()]);
      }
      unbind();
    };
  };

  const emit = (event: string, args?: any): void => {
    if (DEBUG) {
      console.info(
        `Emitting: %c${event}%c ${args ? '- - ' + args : ''}`,
        'font-weight: bold',
        'font-weight: normal'
      );
    }
    lastEmittedValues.set(event, args);
    emitter.emit(event, args);
  };

  const once = (event: string, cb: (args: any) => void, scope?: string) => {
    const unbind = on(
      event,
      (...args) => {
        unbind();
        cb(...args);
      },
      scope
    );

    if (scope) {
      addToScope(scope, unbind);
    }

    if (DEBUG) {
      activeSubscriptions.set(event + '-' + uuidv4(), true);
      console.info(
        `Active subscriptions: %c${activeSubscriptions.size}`,
        'font-weight: bold'
      );
      console.info('Subscribing to: %c' + event + '-' + uuidv4(), 'color: red');
      // console.info([...activeSubscriptions.keys()]);
    }

    return () => {
      if (DEBUG) {
        console.info(
          'Unsubscribing from: %c' + event + '-' + uuidv4(),
          'color: green'
        );
        activeSubscriptions.delete(event + '-' + uuidv4());
        console.info(
          `Active subscriptions: %c${activeSubscriptions.size}`,
          'font-weight: bold'
        );
        console.info([...activeSubscriptions.keys()]);
      }
      unbind();
    };
  };

  instance = {
    on,
    once,
    emit,
    getLastEmittedValue,
    events: emitter.events,
    getEvents: () => emitter.events,
    unbindAll: () => {
      emitter.events = {};
    },
    unbindByScope: (scope: string) =>
      scopedEvents[scope]?.forEach((unbind) => unbind()),
  };

  (window as any).events_manager = instance;

  return instance;
};
